import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Popconfirm,
  message,
  Upload,
  Row,
  Col,
  Statistic,
  Typography,
  Image,
  DatePicker,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CarOutlined,
  UploadOutlined,
  EyeOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Vehicle {
  id: string;
  brand: string;
  model: string;
  plateNumber: string;
  type: 'sedan' | 'suv' | 'mpv' | 'luxury';
  fuelType: 'gasoline' | 'electric' | 'hybrid';
  seats: number;
  status: 'available' | 'rented' | 'maintenance' | 'offline';
  mileage: number;
  dailyRate: number;
  hourlyRate?: number;
  images: string[];
  features: string[];
  registrationDate: string;
  lastMaintenance: string;
  nextMaintenance: string;
  insuranceExpiry: string;
  currentOrder?: string;
  location: string;
}

const VehicleManagement: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingVehicle, setViewingVehicle] = useState<Vehicle | null>(null);
  const [form] = Form.useForm();

  // サンプルデータ
  useEffect(() => {
    setVehicles([
      {
        id: '1',
        brand: 'メルセデス・ベンツ',
        model: 'E300',
        plateNumber: '品川330あ1234',
        type: 'luxury',
        fuelType: 'gasoline',
        seats: 5,
        status: 'available',
        mileage: 15000,
        dailyRate: 450,
        hourlyRate: 60,
        images: ['https://via.placeholder.com/300x200'],
        features: ['本革シート', '自動運転機能', 'ナビゲーション', 'Bluetooth'],
        registrationDate: '2022-03-15',
        lastMaintenance: '2023-10-15',
        nextMaintenance: '2024-01-15',
        insuranceExpiry: '2024-03-15',
        location: '駐車場Aエリア'
      },
      {
        id: '2',
        brand: 'BMW',
        model: 'X5',
        plateNumber: '品川300さ5678',
        type: 'suv',
        fuelType: 'gasoline',
        seats: 7,
        status: 'rented',
        mileage: 28000,
        dailyRate: 680,
        hourlyRate: 85,
        images: ['https://via.placeholder.com/300x200'],
        features: ['パノラマサンルーフ', '4WDシステム', 'シートヒーター', 'バックカメラ'],
        registrationDate: '2021-08-20',
        lastMaintenance: '2023-09-20',
        nextMaintenance: '2023-12-20',
        insuranceExpiry: '2024-08-20',
        currentOrder: 'O20231201001',
        location: 'お客様利用中'
      },
      {
        id: '3',
        brand: 'トヨタ',
        model: 'プリウス',
        plateNumber: '品川500と9012',
        type: 'sedan',
        fuelType: 'hybrid',
        seats: 5,
        status: 'maintenance',
        mileage: 12000,
        dailyRate: 380,
        hourlyRate: 50,
        images: ['https://via.placeholder.com/300x200'],
        features: ['ハイブリッド', '急速充電', 'スマートコネクト', '運転支援'],
        registrationDate: '2022-11-10',
        lastMaintenance: '2023-11-25',
        nextMaintenance: '2024-02-25',
        insuranceExpiry: '2024-11-10',
        location: '整備工場'
      }
    ]);
  }, []);

  const typeColors = {
    sedan: 'blue',
    suv: 'green',
    mpv: 'orange',
    luxury: 'purple'
  };

  const typeLabels = {
    sedan: 'セダン',
    suv: 'SUV',
    mpv: 'ミニバン',
    luxury: '高級車'
  };

  const statusColors = {
    available: 'success',
    rented: 'processing',
    maintenance: 'warning',
    offline: 'default'
  };

  const statusLabels = {
    available: '利用可能',
    rented: 'レンタル中',
    maintenance: 'メンテナンス中',
    offline: 'オフライン'
  };

  const fuelTypeLabels = {
    gasoline: 'ガソリン',
    electric: 'EV（電気）',
    hybrid: 'ハイブリッド'
  };

  const columns = [
    {
      title: '車両情報',
      key: 'vehicle',
      render: (_: any, record: Vehicle) => (
        <Space>
          <div>
            <div><strong>{record.brand} {record.model}</strong></div>
            <div style={{ color: '#666', fontSize: '12px' }}>{record.plateNumber}</div>
          </div>
        </Space>
      )
    },
    {
      title: '車種',
      dataIndex: 'type',
      key: 'type',
      render: (type: keyof typeof typeLabels) => (
        <Tag color={typeColors[type]}>{typeLabels[type]}</Tag>
      )
    },
    {
      title: '燃料タイプ',
      dataIndex: 'fuelType',
      key: 'fuelType',
      render: (fuelType: keyof typeof fuelTypeLabels) => fuelTypeLabels[fuelType]
    },
    {
      title: '定員',
      dataIndex: 'seats',
      key: 'seats',
      render: (seats: number) => `${seats}人乗り`
    },
    {
      title: '稼働状況',
      dataIndex: 'status',
      key: 'status',
      render: (status: keyof typeof statusLabels) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      )
    },
    {
      title: '日額料金',
      dataIndex: 'dailyRate',
      key: 'dailyRate',
      render: (rate: number) => `¥${rate}/日`
    },
    {
      title: '走行距離',
      dataIndex: 'mileage',
      key: 'mileage',
      render: (mileage: number) => `${mileage.toLocaleString()}km`
    },
    {
      title: '現在地',
      dataIndex: 'location',
      key: 'location'
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: Vehicle) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            詳細
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          {record.status === 'available' && (
            <Button
              type="link"
              icon={<ToolOutlined />}
              onClick={() => handleMaintenance(record.id)}
            >
              メンテナンス
            </Button>
          )}
          <Popconfirm
            title="この車両を削除しますか？"
            onConfirm={() => handleDelete(record.id)}
            okText="OK"
            cancelText="キャンセル"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              削除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleAdd = () => {
    setEditingVehicle(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    form.setFieldsValue({
      ...vehicle,
      registrationDate: vehicle.registrationDate,
      lastMaintenance: vehicle.lastMaintenance,
      nextMaintenance: vehicle.nextMaintenance,
      insuranceExpiry: vehicle.insuranceExpiry
    });
    setModalVisible(true);
  };

  const handleView = (vehicle: Vehicle) => {
    setViewingVehicle(vehicle);
    setViewModalVisible(true);
  };

  const handleDelete = (id: string) => {
    setVehicles(prev => prev.filter(item => item.id !== id));
    message.success('削除しました');
  };

  const handleMaintenance = (id: string) => {
    setVehicles(prev => prev.map(item => 
      item.id === id 
        ? { ...item, status: 'maintenance', location: '整備工場' }
        : item
    ));
    message.success('車両をメンテナンス状態にしました');
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingVehicle) {
        // 編集
        setVehicles(prev => prev.map(item => 
          item.id === editingVehicle.id 
            ? { ...item, ...values }
            : item
        ));
        message.success('更新しました');
      } else {
        // 新增
        const newVehicle: Vehicle = {
          id: Date.now().toString(),
          ...values,
          images: ['https://via.placeholder.com/300x200'],
          features: values.features || [],
          currentOrder: undefined
        };
        setVehicles(prev => [newVehicle, ...prev]);
        message.success('追加しました');
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('フォーム検証に失敗しました:', error);
    }
  };

  const totalStats = {
    totalVehicles: vehicles.length,
    availableVehicles: vehicles.filter(v => v.status === 'available').length,
    rentedVehicles: vehicles.filter(v => v.status === 'rented').length,
    maintenanceVehicles: vehicles.filter(v => v.status === 'maintenance').length
  };

  return (
    <div>
      <Title level={2}>車両管理</Title>
      
      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="車両総数"
              value={totalStats.totalVehicles}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="利用可能"
              value={totalStats.availableVehicles}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="レンタル中"
              value={totalStats.rentedVehicles}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="メンテナンス中"
              value={totalStats.maintenanceVehicles}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 車両リスト */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            車両追加
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={vehicles}
          loading={loading}
          rowKey="id"
          pagination={{
            total: vehicles.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]}件 / 全${total}件`
          }}
        />
      </Card>

      {/* 追加/編集モーダル */}
      <Modal
        title={editingVehicle ? '車両編集' : '車両追加'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 'available', fuelType: 'gasoline', type: 'sedan' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="brand"
                label="メーカー"
                rules={[{ required: true, message: '車両メーカーを入力してください' }]}
              >
                <Input placeholder="車両メーカーを入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="model"
                label="モデル"
                rules={[{ required: true, message: '車両モデルを入力してください' }]}
              >
                <Input placeholder="車両モデルを入力" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="plateNumber"
                label="ナンバープレート"
                rules={[{ required: true, message: 'ナンバープレートを入力してください' }]}
              >
                <Input placeholder="ナンバープレートを入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="車種"
                rules={[{ required: true, message: '車種を選択してください' }]}
              >
                <Select placeholder="車種を選択">
                  <Option value="sedan">セダン</Option>
                  <Option value="suv">SUV</Option>
                  <Option value="mpv">ミニバン</Option>
                  <Option value="luxury">高級車</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="fuelType"
                label="燃料タイプ"
                rules={[{ required: true, message: '燃料タイプを選択してください' }]}
              >
                <Select placeholder="燃料タイプを選択">
                  <Option value="gasoline">ガソリン</Option>
                  <Option value="electric">EV（電気）</Option>
                  <Option value="hybrid">ハイブリッド</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="seats"
                label="定員"
                rules={[{ required: true, message: '定員を入力してください' }]}
              >
                <InputNumber min={2} max={9} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="稼働状況"
                rules={[{ required: true, message: '稼働状況を選択してください' }]}
              >
                <Select placeholder="稼働状況を選択">
                  <Option value="available">利用可能</Option>
                  <Option value="maintenance">メンテナンス中</Option>
                  <Option value="offline">オフライン</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="dailyRate"
                label="日額料金(円)"
                rules={[{ required: true, message: '日額料金を入力してください' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="hourlyRate"
                label="時間料金(円)"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="mileage"
                label="走行距離(km)"
                rules={[{ required: true, message: '走行距離を入力してください' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="location"
            label="現在地"
            rules={[{ required: true, message: '現在地を入力してください' }]}
          >
            <Input placeholder="車両の現在地を入力" />
          </Form.Item>
          
          <Form.Item
            name="features"
            label="車両特徴"
          >
            <Select mode="tags" placeholder="車両の特徴機能を入力">
              <Option value="本革シート">本革シート</Option>
              <Option value="自動運転機能">自動運転機能</Option>
              <Option value="ナビゲーション">ナビゲーション</Option>
              <Option value="Bluetooth">Bluetooth</Option>
              <Option value="パノラマサンルーフ">パノラマサンルーフ</Option>
              <Option value="シートヒーター">シートヒーター</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 詳細情報モーダル */}
      <Modal
        title="車両詳細情報"
        open={viewModalVisible}
        footer={null}
        onCancel={() => setViewModalVisible(false)}
        width={600}
      >
        {viewingVehicle && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Image
                  width="100%"
                  src={viewingVehicle.images[0]}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8M+0RVVAU2B8FFd8//yqNjQ3E/+VN9vAElBVU2Xl3Zf7M=="
                />
              </Col>
              <Col span={12}>
                <Space direction="vertical" size="middle">
                  <div>
                    <Title level={4}>{viewingVehicle.brand} {viewingVehicle.model}</Title>
                    <Text type="secondary">{viewingVehicle.plateNumber}</Text>
                  </div>
                  <div>
                    <Tag color={typeColors[viewingVehicle.type]}>{typeLabels[viewingVehicle.type]}</Tag>
                    <Tag color={statusColors[viewingVehicle.status]}>{statusLabels[viewingVehicle.status]}</Tag>
                  </div>
                  <Statistic title="日額料金" value={viewingVehicle.dailyRate} suffix="円/日" />
                  <Statistic title="走行距離" value={viewingVehicle.mileage} suffix="km" />
                </Space>
              </Col>
            </Row>
            
            <div style={{ marginTop: 24 }}>
              <Title level={5}>車両特徴</Title>
              <Space wrap>
                {viewingVehicle.features.map((feature, index) => (
                  <Tag key={index} color="blue">{feature}</Tag>
                ))}
              </Space>
            </div>
            
            <div style={{ marginTop: 16 }}>
              <Title level={5}>メンテナンス情報</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <span style={{ fontWeight: 'bold' }}>前回メンテナンス：</span> {viewingVehicle.lastMaintenance}
                </Col>
                <Col span={12}>
                  <span style={{ fontWeight: 'bold' }}>次回メンテナンス：</span> {viewingVehicle.nextMaintenance}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <span style={{ fontWeight: 'bold' }}>保険満了日：</span> {viewingVehicle.insuranceExpiry}
                </Col>
                <Col span={12}>
                  <span style={{ fontWeight: 'bold' }}>現在地：</span> {viewingVehicle.location}
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleManagement;